import {
	type ColumnFilterValue,
	type Sort2 as Sort,
} from '@innovyze/lib-am-common';
import {
	getAssetDetail,
	getAssetLocation,
	getAssetRisk,
	getAssetRiskHistory,
	getInspectionAttributes,
} from '@innovyze/lib-am-common/Actions';
import { makeMapURL } from '@innovyze/lib-am-common/Utils';
import {
	DataGrid,
	type GridFilterModel,
	GridLogicOperator,
	type GridRowsProp,
	type GridSortModel,
	useGridApiRef,
	useIsFeatureEnabled,
	useTzDateTime,
} from '@innovyze/stylovyze';
import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import {
	changePage,
	changeSearch,
	changePagination,
	changeSort,
	reloadRehabResult,
	changeFiltersSelect,
	clearScanId,
	changeRehabResultsFilter,
	setRefreshResults,
	getGroupResult,
} from '@Actions';
import { useGlobalization } from '@Translations';
import { ROWS_PER_PAGE } from '@Utils/constants';
import { type AssetDetailActions } from './RehabResult.component';
import { useRehabResultSelectors } from './useRehabResultsSelectors';
import { selectRehabResultFilterProps } from '@Selectors';
import { tableColumns } from './TableColumns';
import { TableToolbar } from './TableToolbar.component';
import CanvasIcon from '@Components/Tools/CanvasIcon.svg';
import {
	type AssetTypeId,
	type SystemTypeId,
} from '@innovyze/lib_asset_schema';

interface AssetIdAndType {
	assetId: string;
	assetType: AssetTypeId;
}

interface RehabResultTableProps {
	readonly systemType: SystemTypeId;
	readonly assetType: AssetTypeId;
	readonly treeId: string;
	readonly lastRunCompleted?: string;
	readonly assetAction?: AssetDetailActions;
}

export const removeInvalidEntries = (filterModel: GridFilterModel) => {
	const items = filterModel?.items?.filter(
		item =>
			(item.value && item.value.length !== 0) ||
			((item.operator == 'isEmpty' || item.operator == 'isNotEmpty') &&
				!item.value),
	);
	return {
		...filterModel,
		items,
	};
};

export default function RehabResultTable({
	systemType,
	assetType,
	treeId,
	lastRunCompleted,
	assetAction,
}: RehabResultTableProps) {
	// library hooks
	const { t } = useGlobalization();
	const dispatch = useDispatch();
	const history = useHistory();
	const filters = selectRehabResultFilterProps(false);
	const apiRef = useGridApiRef();

	// custom hooks
	const formatDateUTC = useTzDateTime({ timeZone: 'UTC' }).formatDate;

	// feature flags
	const isFindAssetsEnabled = !!useIsFeatureEnabled(
		'info-360-navigate-to-asset',
	);

	const groupTablePage = useRehabResultSelectors(true).tablePage;

	React.useEffect(() => {
		dispatch(getGroupResult());
	}, [treeId]);

	React.useEffect(() => {
		console.log(groupTablePage);
	}, [groupTablePage]);

	// selectors
	const {
		tablePage,
		isLoading,
		isWaiting,
		assetLocation,
		rehabResultsPagination,
		search,
		filter,
		columnVisibilityModel,
		setColumnVisibilityModel,
		refreshResults,
	} = useRehabResultSelectors(false);

	const paginationModel = React.useMemo(() => {
		return {
			page: (rehabResultsPagination.page ?? 1) - 1,
			pageSize: rehabResultsPagination.slice ?? 10,
		};
	}, [rehabResultsPagination]);

	const [filterModel, setFilterModel] = React.useState<
		GridFilterModel | undefined
	>(filter);

	const [sortModel, setSortModel] = React.useState<GridSortModel>([
		{
			field: 'FINAL_RRHAB_ACTION',
			sort: tablePage.sortDescending ? 'desc' : 'asc',
		},
	]);

	const [findAsset, setFindAsset] = React.useState<
		AssetIdAndType | undefined
	>(undefined);

	const onRefresh = () => {
		dispatch(clearScanId({}));
		dispatch(changePage({ page: tablePage.page, forGroupResults: false }));
	};

	/*
	 * This useEffect hook navigates to a new URL when `assetLocation` or `findAsset` changes.
	 * The URL is generated based on the asset's type, id, and position.
	 * After navigation, it resets `findAsset` to its initial state.
	 */
	React.useEffect(() => {
		if (
			findAsset &&
			assetLocation &&
			assetLocation.assetId === findAsset.assetId
		) {
			const url = makeMapURL(
				findAsset.assetType.id,
				findAsset.assetId,
				assetLocation.position,
			);

			if (url) history.push(url);

			setFindAsset(undefined);
		}
	}, [assetLocation, findAsset]);

	React.useEffect(() => {
		setFilterModel(filter);
		if (filter === undefined) {
			apiRef.current.setFilterModel({
				items: [],
				logicOperator: GridLogicOperator.And,
			});
		}
	}, [filter]);

	React.useEffect(() => {
		if (refreshResults) {
			onRefresh();
			dispatch(setRefreshResults(false));
		}
	}, [refreshResults]);

	// actions
	const dispatchSearchFn = (text: string) =>
		dispatch(changeSearch({ search: text, forGroupResults: false }));
	const dispatchSortFn = (name: string, desc: boolean) => {
		const sortPayload: Sort = {
			key: name,
			descending: desc,
		};
		dispatch(changeSort({ sort: sortPayload, forGroupResults: false }));
	};

	const handleOpen = (
		assetId: string,
		systemType: SystemTypeId,
		assetType: AssetTypeId,
	) => {
		if (assetAction?.onAssetDetailClicked) {
			assetAction.onAssetDetailClicked(assetId, systemType, assetType);

			dispatch(
				getAssetDetail({
					assetId: assetId,
					assetType: assetType.id,
					formatDateUTC: formatDateUTC,
					systemType: systemType.id,
				}),
			);

			dispatch(
				getAssetRisk({
					assetId: assetId,
					assetType: assetType.id,
				}),
			);

			dispatch(
				getAssetRiskHistory({
					assetId: assetId,
					assetType: assetType.id,
				}),
			);

			dispatch(
				getInspectionAttributes({
					assetId: assetId,
					assetType: assetType.id,
				}),
			);

			dispatch(
				getInspectionAttributes({
					assetId: assetId,
					assetType: assetType.id,
				}),
			);
		}
	};

	const onFindAsset = (assetId: string) => {
		const mapAsset = {
			assetId: assetId,
			assetType: assetType,
		};
		setFindAsset(mapAsset);

		const mapAssetGet = {
			assetId: assetId,
			assetType: assetType.id,
		};
		dispatch(getAssetLocation(mapAssetGet));
	};

	const rows: GridRowsProp =
		tablePage.cells.map((row: string[], nRow: number) => {
			return {
				id: nRow,
				ASSET_ID: row[1],
				FINAL_RRHAB_ACTION: row[2],
				NOTES: row[6],
			};
		}) || [];

	const initialColumnsLayout = useMemo(() => {
		return tableColumns(
			t,
			filters,
			handleOpen,
			isFindAssetsEnabled,
			onFindAsset,
			groupTablePage.cells,
			systemType,
			assetType,
		);
	}, [treeId, groupTablePage, systemType, assetType]);

	const initialSortModel: GridSortModel = React.useMemo(() => {
		if (rehabResultsPagination && rehabResultsPagination.sortKey)
			return [
				{
					field: rehabResultsPagination.sortKey,
					sort: rehabResultsPagination.sortDescending
						? 'desc'
						: 'asc',
				},
			];
		else
			return [
				{
					// Default sort is ASSET_ID asc
					field: 'ASSET_ID',
					sort: 'asc',
				},
			];
	}, [rehabResultsPagination]);

	const filterRef = useMemo(() => React.createRef<HTMLButtonElement>(), []);

	const showFilterPanel = () => {
		if (apiRef.current.showFilterPanel) apiRef.current.showFilterPanel();
	};

	return (
		<DataGrid
			apiRef={apiRef}
			weave={true}
			columns={initialColumnsLayout}
			initialState={{
				pagination: {
					paginationModel: paginationModel,
				},
				sorting: {
					sortModel: initialSortModel,
				},
				filter: {
					filterModel: filterModel,
				},
			}}
			rows={rows}
			data-cy="asset-details-table"
			pinnedColumns={{ left: ['Locked', 'ASSET_ID'], right: ['Menu'] }}
			className={isWaiting ? 'wait' : 'ScrollTable'}
			// header settings
			refreshButton={{ onRefresh: onRefresh }}
			filterToolbar
			filterMode="server"
			onFilterModelChange={(model: GridFilterModel) => {
				// remove the invalid filters as the filter model change event
				// is triggered when the modal is poppped up
				const validFilter = removeInvalidEntries(model);
				if (
					// first time filter modal is intialised check there are valid filters to send
					(!filterModel && validFilter?.items.length) ||
					// afterwards check if the filter has changed before sending
					(filterModel &&
						JSON.stringify(filterModel) !==
							JSON.stringify(validFilter))
				) {
					const finalRehabs = validFilter.items
						?.filter(item => item.field === 'FINAL_RRHAB_ACTION')
						?.reduce((acc: string[], val) => {
							if (val?.value) acc.push(...(val.value ?? []));
							return acc;
						}, [])
						// remove duplicates
						?.filter(
							(value, index, self) =>
								self.indexOf(value) === index,
						);

					const filter: ColumnFilterValue[] = [
						{
							colKey: 'FINAL_RRHAB_ACTION',
							value: finalRehabs,
						},
					];

					dispatch(changeFiltersSelect(filter));
					dispatch(changeRehabResultsFilter(validFilter));
				}
			}}
			slots={{
				toolbar: TableToolbar,
			}}
			slotProps={{
				toolbar: {
					lastRunCompleted: lastRunCompleted,
					disable: isLoading || isWaiting,
					showFilterPanel: showFilterPanel,
					refreshList: onRefresh,
					filterRef: filterRef,
				},
			}}
			search={{
				dataCy: 'search-01',
				onChange: query => {
					dispatchSearchFn(query);
					dispatch(reloadRehabResult());
				},
				onRequestSearch: () => {
					dispatch(reloadRehabResult());
				},
				onCancelSearch: () => {
					dispatchSearchFn('');
					dispatch(reloadRehabResult());
				},
				debounce: true,
				placeholder: t('Search'),
				searchInProgress: isLoading,
				value: search,
			}}
			loading={isLoading}
			skeletonRowCount={paginationModel.pageSize}
			footerHelper={
				isLoading
					? ''
					: t('{{from}}-{{to}} of {{total}}', {
							from:
								(rehabResultsPagination.page - 1) *
									rehabResultsPagination.slice +
								1,
							to:
								rehabResultsPagination.page ==
								rehabResultsPagination.pages
									? rehabResultsPagination.total
									: rehabResultsPagination.page *
										rehabResultsPagination.slice,
							total: rehabResultsPagination.total,
						})
			}
			emptyState={{
				title: t('There are no assets groups'),
				subtitle: t(
					'Configure a tree and run it so see the asset groups.',
				),
				icon: <img src={CanvasIcon} alt={t('Decision Tree Icon')} />,
				color: '#4A6067',
			}}
			disableMultipleColumnsSorting
			disableColumnReorder
			// pagination and rows per page settings
			paginationMode="server"
			paginationModel={paginationModel}
			onPaginationModelChange={_paginationModel => {
				if (paginationModel.page === _paginationModel.page) {
					// paginationModel didn't change, so we need to reset the page to 0
					// this way the table will start from the first page
					_paginationModel.page = 0;
				}
				dispatch(
					changePagination({
						page: _paginationModel.page + 1,
						slice: _paginationModel.pageSize,
						forGroupResults: false,
					}),
				);
			}}
			rowCount={tablePage.total}
			pagination
			pageSizeOptions={ROWS_PER_PAGE}
			sortingOrder={['desc', 'asc']}
			sortingMode="server"
			onSortModelChange={sm => {
				if (!sm.length) {
					if (!sortModel.length) {
						return sm.push({ field: 'ASSET_ID', sort: 'asc' });
					} else {
						const sortedField = sortModel?.[0];
						sortedField.sort =
							sortedField.sort === 'asc' ? 'desc' : 'asc';
						sm.push(sortModel[0]);
					}
				}

				sm.forEach(sort => {
					const key = sort?.field;
					const descending = sort?.sort !== 'asc';
					dispatchSortFn(key, descending);
				});

				if (!sm.length) dispatchSortFn('ASSET_ID', false);
				setSortModel(sm);
			}}
			// slotProps={{
			// 	filterPanel: {
			// 		logicOperators: [GridLogicOperator.And],
			// 	},
			// }}
			onColumnVisibilityModelChange={setColumnVisibilityModel}
			columnVisibilityModel={columnVisibilityModel}
			disableColumnSelector
		/>
	);
}
